import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { DownOutlined } from '@ant-design/icons';
import { Flex } from '@revenuemarket/cds';
import { theme } from '@revenuemarket/design';
import { ICellEditorComp, ICellEditorParams } from 'ag-grid-community';
import { Divider, Select } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';

interface Props<T> extends ICellEditorParams<T, number> {
  options: (DefaultOptionType & { description?: string })[];
  footer?: React.ReactNode;
  onValueChange?: (value: any) => void;
}

const AGGridSingleSelectCellEditor = <T,>(
  { value, options, api, node, column, footer, onValueChange }: Props<T>,
  ref: ForwardedRef<ICellEditorComp>,
) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [innerValue, setInnerValue] = useState(value ?? undefined);
  const [open, setOpen] = useState(true);

  useImperativeHandle(
    ref,
    () => ({
      getGui: () => containerRef.current ?? document.body,
      getValue: () => innerValue,
    }),
    [innerValue],
  );

  useEffect(() => {
    if (!open) {
      api.stopEditing(false);
      api.setFocusedCell(node.rowIndex as number, column);
    }
  }, [api, column, node.rowIndex, open]);

  return (
    <Flex ref={containerRef} align="center" style={{ height: '100%' }}>
      <Select
        autoFocus
        open={open}
        onDropdownVisibleChange={setOpen}
        options={options}
        value={innerValue}
        onChange={(value) => {
          setInnerValue(value);
          onValueChange?.(value);
        }}
        showSearch
        optionFilterProp="label"
        bordered={false}
        suffixIcon={<DownOutlined style={{ color: theme.colorTokens.iconPlaceholder }} />}
        style={{ width: '100%' }}
        dropdownRender={(menu) => (
          <>
            {menu}

            {footer && (
              <>
                <Divider style={{ margin: '8px 0' }} />
                <Flex justify="flex-end" style={{ padding: '8px' }}>
                  {footer}
                </Flex>
              </>
            )}
          </>
        )}
      />
    </Flex>
  );
};

export default forwardRef(AGGridSingleSelectCellEditor);
